<template>
    <card title="Reset Patient App Password">
        <div style="margin-bottom: 15px; margin-left: 20px">
            <b-row>
                <p>
                    An auto generated temporary password will be sent to this patient's email and/or
                    SMS.
                </p>

                <p>
                    Once the temporary password is used it will prompt the patient to create a
                    permanent password on the Patient App.
                </p>

                <p>Are you sure you want to reset the patient app password?</p>
                <NewClearSaveButtons :titles="_buttonTitles" :disable-save="!isFormValid"
                    @cancel-clicked="$emit('close')" @save-clicked="updatePassword" />
            </b-row>
        </div>
    </card>
</template>
<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import NewClearSaveButtons, { NewClearSaveTitles } from "@/components/NewClearSaveButtons.vue";
import NotificationTypes = NotificationOptions.NotificationTypes;

type DeliveryOutcome = "NotAttempted" | "Sent" | "Failed";

interface PasswordResetResult {
    email: DeliveryOutcome;
    text: DeliveryOutcome;
}

@Component({
    name: "ResetPatientAppPasswordComponent",
    components: { NewClearSaveButtons },
})
export default class ResetPatientAppPasswordComponent extends Vue {
    @Prop() private patient!: Patient;

    private isSubmitting = false;

    updatePassword() {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        axios
            .post<PasswordResetResult>(`Patient/reset-patientApp-password`, this.patient)
            .then((res) => {
                // The API reports a per-channel outcome (sent / failed / not-attempted). List the channels
                // that actually sent so the user knows exactly how it was delivered, and warn only about a
                // channel that was attempted and failed — never one that was simply skipped (no address on
                // file, disabled, or opted out), which would otherwise read as a false delivery failure.
                const labels: Record<keyof PasswordResetResult, string> = {
                    email: "email",
                    text: "text message",
                };
                const channels = Object.keys(labels) as (keyof PasswordResetResult)[];
                const sent = channels.filter((c) => res.data[c] === "Sent").map((c) => labels[c]);
                const failed = channels.filter((c) => res.data[c] === "Failed").map((c) => labels[c]);

                const sentVia = sent.join(" and ");
                if (failed.length === 0) {
                    this.$notification(
                        NotificationOptions.success(`Temporary password sent by ${sentVia}.`, "Password Reset"),
                    );
                } else if (sent.length > 0) {
                    this.$notification(
                        NotificationOptions.warning(
                            `Temporary password sent by ${sentVia}, but the ${failed.join(
                                " and ",
                            )} could not be delivered.`,
                        ),
                    );
                } else {
                    // The API returns an error when nothing sends, so this is a defensive fallback only.
                    this.$notification(
                        NotificationOptions.warning("Temporary password could not be delivered."),
                    );
                }
                this.$emit("close");
            })
            .catch((err) => {
                console.warn(err);
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    private get _buttonTitles() {
        let res: NewClearSaveTitles;
        res = {
            save: "Reset Password",
            clear: "",
            new: "",
            cancel: "Cancel",
        };
        return res;
    }

    get isFormValid() {
        return !this.isSubmitting;
    }
}
</script>
